import { useEffect, useMemo, useState } from "react";
import { Ban } from "lucide-react";
import {
  EntryPickerOverlay,
  PickerRow,
  PickerSectionHeading,
} from "./EntryPickerOverlay";
import {
  accountColor,
  accountPrimaryLabel,
  accountSecondaryLabel,
  type PickerAccount,
} from "./pickerHelpers";

interface AccountPickerSheetProps {
  open: boolean;
  accounts: PickerAccount[];
  value: string;
  onSelect: (id: string) => void;
  onClose: () => void;
}

/**
 * Full-screen account picker. Same row anatomy as the category picker —
 * colour disc, label, optional sub-label — so the two feel like one system.
 *
 * `accountId` is nullable on Income/Expense, so "No account" is a real
 * choice and gets its own row rather than being an empty state.
 */
export function AccountPickerSheet({
  open,
  accounts,
  value,
  onSelect,
  onClose,
}: AccountPickerSheetProps) {
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- deliberate open-time reset of transient search state
    setQuery("");
    setSearchOpen(false);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      q
        ? accounts.filter((a) =>
            `${accountPrimaryLabel(a)} ${accountSecondaryLabel(a)}`.toLowerCase().includes(q),
          )
        : accounts,
    [accounts, q],
  );

  function choose(id: string) {
    onSelect(id);
    onClose();
  }

  return (
    <EntryPickerOverlay
      open={open}
      title="Account"
      onBack={onClose}
      query={query}
      onQueryChange={setQuery}
      searchOpen={searchOpen}
      onSearchOpenChange={setSearchOpen}
      settingsHref="/accounts"
      settingsLabel="Manage accounts"
    >
      <PickerSectionHeading>Accounts</PickerSectionHeading>
      {filtered.map((a) => (
        <PickerRow
          key={a.id}
          color={accountColor(a)}
          label={accountPrimaryLabel(a)}
          sublabel={accountSecondaryLabel(a) || undefined}
          selected={a.id === value}
          onClick={() => choose(a.id)}
        />
      ))}
      {filtered.length === 0 && (
        <p className="px-4 py-6 text-sm text-[var(--cp-ink-3)]">
          {q ? "No matching accounts" : "No accounts yet."}
        </p>
      )}

      {/* Unlinked entries are valid — accountId is nullable on both models. */}
      {!q && (
        <button
          type="button"
          onClick={() => choose("")}
          aria-pressed={value === ""}
          className="flex min-h-12 w-full items-center gap-3 border-b border-[var(--cp-line-2)] px-4 py-2 text-left transition-colors hover:bg-[var(--cp-surface-2)]"
        >
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--cp-surface-2)] text-[var(--cp-ink-3)]"
            aria-hidden
          >
            <Ban className="h-4 w-4" />
          </span>
          <span className="flex-1 text-[15px] text-[var(--cp-ink-2)]">No account</span>
        </button>
      )}
    </EntryPickerOverlay>
  );
}
